import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useId, lazy } from 'react';
import { RevealOnScroll } from '@worldcoo/ui/components/reveal-on-scroll';
import { Seo, resolveSlugSeo } from '../-components/seo';
import { Layout, Paragraph, Title } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { ChevronDown } from 'lucide-react';
import { cn, formatNumber, formatCompactNumber } from '@worldcoo/ui/lib/utils';
import { useLingui } from '@lingui/react/macro';
import { Image } from '../-components/image';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { slugs } from './round-up.meta';
import { wwwLocales } from './-components/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { WwwPagePending } from './-components/page-pending';

const Newsletter = lazy(() => import('./-components/newsletter').then((m) => ({ default: m.Newsletter })));

export const Route = createFileRoute('/www/round-up')({
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(
			trpc.portal.getWorldcooGlobalStats.queryOptions(undefined, { staleTime: 60 * 60 * 1000 }),
		);
	},
	component: Component,
	pendingComponent: WwwPagePending,
});

function Component() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	const formattedStoresCount = formatNumber(language, stats.storesCount, 0);

	const formattedDonationsCount = formatCompactNumber(language, stats.totalDonations);

	const { baseUrl, locale, hasLocalePrefix } = Route.useRouteContext();
	const { canonicalUrl, alternates } = resolveSlugSeo(hasLocalePrefix, baseUrl, locale, wwwLocales, slugs);

	const [featureOpen, setFeatureOpen] = useState<string | null>(null);
	const toggleFeature = (id: string) => setFeatureOpen(featureOpen === id ? null : id);

	return (
		<Layout>
			<Header />
			<Seo
				title={t`Charity Rounding Up - Worldcoo`}
				description={t`An innovative, quick and transparent tool on a card machine that allows shoppers to make a small monetary contribution when paying by card.`}
				canonicalUrl={canonicalUrl}
				locale={locale}
				alternateLocales={[...wwwLocales]}
				alternates={alternates}
			/>

			{/* Hero Section + Why Section (shared background) */}
			<section className="bg-hero px-8 py-16">
				{/* Hero Content */}
				<div className="mx-auto flex max-w-6xl flex-col items-center gap-8 md:flex-row md:items-center">
					{/* Text content */}
					<div className="flex flex-1 flex-col gap-4 text-center md:max-w-lg md:text-left">
						<Title>{t`Charity Rounding Up, micro-donations that add up`}</Title>
						<Paragraph>{t`An innovative, quick and transparent tool on a card machine that allows shoppers to make a small monetary contribution when paying by card. Also available for digital payments.`}</Paragraph>

						{/* Stats */}
						<div className="mt-6 flex flex-col gap-6 sm:flex-row sm:gap-10">
							<div className="text-center md:text-left">
								<h2 className="text-2xl font-semibold md:text-3xl">+{formattedStoresCount}</h2>
								<p className="mt-1">{t`Stores offer Charity Rounding Up`}</p>
							</div>
							<div className="text-center md:text-left">
								<h2 className="text-2xl font-semibold md:text-3xl">{formattedDonationsCount}</h2>
								<p className="mt-1">{t`Donations received`}</p>
							</div>
						</div>
					</div>

					{/* Hero Image */}
					<div className="relative flex flex-1 items-center justify-center md:justify-end">
						<Image
							src="/worldcoo-terminal.png"
							aspectRatio={448 / 751}
							height={751}
							alt={t`A payment terminal displaying the Worldcoo Charity Rounding Up donation option`}
							className="-my-36 h-auto w-full max-w-sm md:-mr-8 md:max-w-md"
						/>
					</div>
				</div>

				{/* Why Section (inside hero) */}
				<div className="mx-auto mt-36 max-w-6xl">
					<div className="mb-12 text-center">
						<h2 className="text-primary text-xs font-semibold tracking-wider uppercase md:text-sm">
							{t`Why use Charity Rounding Up?`}
						</h2>
						<p className="mt-4 text-xl font-medium md:text-2xl lg:text-3xl">{t`A simple, free and multi-channel tool`}</p>
					</div>

					<div className="flex flex-col gap-4">
						{/* First row - 3 items */}
						<div className="grid gap-4 md:grid-cols-3">
							<Feature
								title={t`100% free of charge`}
								description={t`At Worldcoo, we assume the costs of enabling, maintaining and adapting the technology to each retailer's payment gateway. We also provide regular reports on the donations received.`}
								isOpen={featureOpen === 'free'}
								onToggle={() => toggleFeature('free')}
							/>
							<Feature
								title={t`Multi-channel and flexible`}
								description={t`A solution that adapts to all retail points of sale and their digital channels such as e-commerce and apps. The retailer also decides how to configure the rounding up system or whether they prefer to establish a set donation.`}
								isOpen={featureOpen === 'multichannel'}
								onToggle={() => toggleFeature('multichannel')}
							/>
							<Feature
								title={t`More loyalty`}
								description={t`Creating new channels of charity collaboration helps grow customer and employee loyalty. According to a recent report by Mastercard, 66% of consumers make decisions based on a brand's commitment to sustainability.`}
								isOpen={featureOpen === 'loyalty'}
								onToggle={() => toggleFeature('loyalty')}
							/>
						</div>

						{/* Second row - 2 items centered */}
						<div className="grid gap-4 md:mx-auto md:max-w-[66%] md:grid-cols-2">
							<Feature
								title={t`Positive social impact`}
								description={t`A proposal for social projects aligned with the retailer's Corporate Social Responsibility policies and with the 17 Sustainable Development Goals of the UN 2030 Agenda. The system can also be upgraded quickly to respond to emergencies.`}
								isOpen={featureOpen === 'impact'}
								onToggle={() => toggleFeature('impact')}
							/>
							<Feature
								title={t`Simple and transparent`}
								description={t`No impact on the checkout queue, intuitive and fast. 100% of the total money raised goes to the social entity. Once the project has been funded, Worldcoo invoices the social entities for their fundraising services. What's more, the donor can request their donation certificate at any time.`}
								isOpen={featureOpen === 'transparent'}
								onToggle={() => toggleFeature('transparent')}
							/>
						</div>
					</div>
				</div>
			</section>

			{/* CTA Section (white background) */}
			<section className="bg-background py-16 md:py-24">
				<div className="mx-auto max-w-6xl px-8">
					<h2 className="mb-12 text-center text-xl font-bold md:text-2xl lg:text-3xl">{t`Start using Charity Rounding Up`}</h2>

					<div className="grid gap-8 md:grid-cols-2">
						{/* NGO Card */}
						<div className="bg-secondary-hero flex flex-col gap-4 rounded-2xl p-8">
							<h3 className="text-lg font-bold md:text-xl">{t`Register your NGO to raise funds`}</h3>
							<p className="flex-1 text-sm md:text-base">{t`Contact us and our project team will reach you to explain to you how we work and how to onboard your NGO.`}</p>
							<Link
								to="/www/contact"
								className="bg-primary text-primary-foreground hover:bg-primary/90 mt-4 inline-block self-start rounded-lg px-6 py-2.5 text-center text-sm font-medium transition-colors md:px-8 md:py-3 md:text-base"
							>
								{t`Contact us`}
							</Link>
						</div>

						{/* Merchant Card */}
						<div className="bg-secondary-hero flex flex-col gap-4 rounded-2xl p-8">
							<h3 className="text-lg font-bold md:text-xl">{t`Activate Charity Rounding Up at your store`}</h3>
							<p className="flex-1 text-sm md:text-base">{t`Contact us so we can study your case and find a way to activate the fundraising system on your payment gateway.`}</p>
							<Link
								to="/www/contact"
								className="bg-primary text-primary-foreground hover:bg-primary/90 mt-4 inline-block self-start rounded-lg px-6 py-2.5 text-center text-sm font-medium transition-colors md:px-8 md:py-3 md:text-base"
							>
								{t`Contact us`}
							</Link>
						</div>
					</div>
				</div>
			</section>

			<RevealOnScroll>
				<Newsletter />
			</RevealOnScroll>
			<Footer />
		</Layout>
	);
}

function Feature(props: { title: string; description: string; isOpen: boolean; onToggle: () => void }) {
	const id = useId();
	const contentId = `feature-content-${id}`;

	return (
		<div className="flex flex-col">
			<button
				type="button"
				onClick={props.onToggle}
				aria-expanded={props.isOpen}
				aria-controls={contentId}
				className={cn(
					'flex w-full items-center justify-center gap-2 py-4 text-center transition-colors',
					'hover:text-primary text-2xl font-medium',
				)}
			>
				<span>{props.title}</span>
				<ChevronDown
					className={cn('h-4 w-4 shrink-0 transition-transform duration-200', props.isOpen && 'rotate-180')}
					aria-hidden
				/>
			</button>
			<div
				id={contentId}
				role="region"
				aria-hidden={!props.isOpen}
				className={cn(
					'grid transition-all duration-200 ease-in-out',
					props.isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
				)}
			>
				<div className="overflow-hidden">
					<p className="px-2 pb-4 text-center text-lg leading-relaxed">{props.description}</p>
				</div>
			</div>
		</div>
	);
}
